'use client'

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import type { RouterOutputs } from '@package/api'
import type { SelectOrganization } from '@package/drizzle/schema'
import { Button, NumberFlow, Skeleton, toast } from '@ui/web/components'
import { WhatsappIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import { WHATSAPP_LINK } from '@helper/utils/core'
import { offsetDate } from '@helper/utils/date'
import { buildUrl, formatCurrency, formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { SeatAssignmentValue } from '../../../lib'
import { useSharedTRPC } from '../../../context'
import { AssignSeatDialog, PriceCardGroup, VoucherList } from '../../../lib'
import { AnimatedCurrency } from './animated-currency'
import { CheckoutCard, CheckoutReveal } from './checkout-card'
import { SAVINGS_EXPLANATION, SavingsLine } from './savings-line'
import {
  calculateAmountPaid,
  calculateSavings,
  calculateTierSavings,
  calculateTotalDiscount,
  calculateTotalPriceWithoutVoucher,
  calculateVoucherDiscount,
  FAMILY_MINIMUM_SEATS,
  longerTermUpsell,
  nextTierUpsell,
  pricesForSeats,
  repointPriceToTier,
  SCHOOL_MINIMUM_SEATS,
} from './util'

interface RenewalFormProps {
  /** The pool being continued. Its end date is the day the renewed seats start. */
  currentMembership: RouterOutputs['myOrganization']['getSeatPools'][number]
  organizationData: Pick<SelectOrganization, 'id' | 'name'>
  getAllPrice: RouterOutputs['price']['getActivePrices']
  type: 'web' | 'school'
  onNavigate: (url: string) => void
}

/**
 * "Perpanjang Premium" — buys the NEXT term, which starts the day this one ends.
 *
 * A renewal is two choices in one purchase, and they are priced as one order:
 *  - which seated students CONTINUE onto the new term, and
 *  - how many extra EMPTY seats to buy for students who are not seated yet.
 *
 * Neither is the old renewal, which moved a pool's end date and therefore had to carry every seat
 * in it at exactly the size it already was. Nothing here touches the current pool: its seats keep
 * running to their own end date, and a student left unselected simply lapses then.
 *
 * The CONTINUING students' seats do not activate on payment. They begin when the current pool
 * closes, which is what the roster's Akses column reports for each of them.
 *
 * The EMPTY seats do. A seat bought for a student not yet enrolled is no use if it cannot be handed
 * out until next term, so those behave like a top-up placed today: they ride the current term from
 * payment and run through the renewed one, and the days in between are charged pro-rata on top of
 * the term. That part of the price comes from the server (`price.getRenewalBridgeQuote`), because
 * the pools bridged were each bought at their own rate and no client can multiply that out; the
 * term itself is priced from the rate card here, as the NEW form does.
 */
export function RenewalForm({ currentMembership, organizationData, getAllPrice, type, onNavigate }: RenewalFormProps) {
  const trpc = useSharedTRPC()

  const minSeats = type === 'school' ? SCHOOL_MINIMUM_SEATS : FAMILY_MINIMUM_SEATS

  // Who sits in the pool today — the only students a renewal can continue.
  const seatedChildrenQuery = useQuery(
    trpc.myOrganization.getPoolSeatedChildren.queryOptions({ membershipId: currentMembership.id }),
  )

  // Names for those ids. The roster read the rest of the product already uses, so a child reads the
  // same here as in the seat list they came from.
  /**
   * The roster, with the seat-flavour fields the dialog needs.
   *
   * Two groups can take a renewed seat: the students already sitting in the pool (they CONTINUE —
   * their current seat ends exactly where the new one starts, so the windows do not overlap) and
   * students holding no seat at all (they JOIN next term). The dialog is handed both as its
   * offerable list, because its own "has a seat today" rule would hide the first group entirely.
   */
  const childrenQuery = useQuery(
    trpc.myOrganization.listChildren.queryOptions({
      pageSize: 'all',
      includeSchoolClass: true,
      includeAllSchoolClasses: true,
      includeSeatFlavour: true,
    }),
  )

  /**
   * Seated students split by whether a renewal can actually carry them.
   *
   * A student already covered past this pool's end — a parent's own subscription, usually — cannot
   * hold the renewed seat too: one child, one seat per window, so they are dropped from the picker
   * rather than shown locked. They used to be named under the count as well; that note went with
   * the rest of the card's supporting prose.
   */
  const seatedChildren = useMemo(() => {
    const seats = seatedChildrenQuery.data ?? []
    const seatedIds = new Set(seats.map((seat) => seat.childId))
    const coveredIds = new Set(seats.filter((seat) => seat.isCoveredElsewhere).map((seat) => seat.childId))

    return (childrenQuery.data?.data ?? [])
      .filter((child) => seatedIds.has(child.id) && !coveredIds.has(child.id))
      .map((child) => ({ id: child.id, name: child.name, image: child.image }))
  }, [seatedChildrenQuery.data, childrenQuery.data])

  /**
   * Who the dialog may offer: the students SITTING IN THE POOL being renewed, and nobody else.
   *
   * A renewal continues an existing term — it names who keeps their seat, not who gets one. A
   * student with no seat today is a new purchase (or an add-on), and offering them here would sell
   * a seat that only starts when the current term ends while they sit Terbatas until then.
   *
   * Handed to the dialog explicitly because its own rule is "does this child hold a seat today",
   * which is exactly inverted for a renewal: every continuing student holds one. Students covered
   * elsewhere past this pool's end are already filtered out of `seatedChildren` and named under the
   * count instead — one child, one seat per window.
   */
  const offerableChildIds = useMemo(() => seatedChildren.map((child) => child.id), [seatedChildren])

  /**
   * `null` until the seated list arrives, so the default (everyone continues) cannot be mistaken for
   * a deliberate "continue nobody" and priced as such while the query is still in flight.
   */
  const [continuedIds, setContinuedIds] = useState<string[] | null>(null)
  const [extraSeats, setExtraSeats] = useState(0)
  const [selectedVoucherId, setSelectedVoucherId] = useState<string | null>(null)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)

  useEffect(() => {
    if (seatedChildrenQuery.data === undefined || childrenQuery.data === undefined) return

    setContinuedIds((held) => held ?? seatedChildren.map((child) => child.id))
  }, [seatedChildrenQuery.data, childrenQuery.data, seatedChildren])

  const isSelectionSettled = continuedIds !== null
  const continuedCount = continuedIds?.length ?? 0
  const seats = continuedCount + extraSeats

  /**
   * The rate card at the seat count being bought. The stepper and the picker both move it, so the
   * tier is re-narrowed on every change — `computeCheckoutCharge` resolves it again server-side and
   * that is what is charged, so a form offering the whole card would quote prices it cannot honour.
   */
  const tierPrices = useMemo(
    () => pricesForSeats(getAllPrice, Math.max(seats, minSeats)),
    [getAllPrice, seats, minSeats],
  )

  const [selectedPrice, setSelectedPrice] = useState<RouterOutputs['price']['getActivePrices'][number] | undefined>(
    () => pricesForSeats(getAllPrice, minSeats)[pricesForSeats(getAllPrice, minSeats).length - 1],
  )

  // Keep the buyer's TERM and swap the tier when the seat count crosses a boundary.
  useEffect(() => {
    setSelectedPrice((held) => (held ? repointPriceToTier(getAllPrice, held, Math.max(seats, minSeats)) : held))
  }, [getAllPrice, seats, minSeats])

  const checkoutRenewalMutation = useMutation(
    trpc.invoice.createInvoice.mutationOptions({
      onSuccess: (data) => {
        if (data.xenditInvoice) {
          onNavigate(data.xenditInvoice.invoiceUrl)
        } else {
          const successUrl = buildUrl(type, '/checkout/success', {
            invoice_id: data.invoice.id,
            type: 'RENEWAL',
            membership_id: currentMembership.id,
          }).href
          onNavigate(successUrl)
        }
      },
    }),
  )

  const { data: vouchersData } = useQuery(trpc.price.getActiveVouchers.queryOptions())

  const selectedVoucher = vouchersData?.find((voucher) => voucher.id === selectedVoucherId) ?? null

  /**
   * What the empty seats cost between payment and the handover, from the server.
   *
   * Asked for even at zero empty seats: the parts say whether empty seats WOULD start today (a
   * lapsed pool has nothing to ride), which the dialog's copy needs before the buyer has added
   * any. `totalPrice` is zero until they do.
   */
  const bridgeQuoteQuery = useQuery({
    ...trpc.price.getRenewalBridgeQuote.queryOptions({ membershipId: currentMembership.id, emptySeats: extraSeats }),
    enabled: isSelectionSettled,
  })

  const bridgeQuote = bridgeQuoteQuery.data
  const bridgeApplies = (bridgeQuote?.parts.length ?? 0) > 0
  const bridgeTotal = extraSeats > 0 ? (bridgeQuote?.totalPrice ?? 0) : 0
  // The total cannot be shown until the bridge is known. Only while it matters — with no empty
  // seats there is nothing to wait for.
  const isBridgeUnresolved = extraSeats > 0 && (bridgeQuoteQuery.isPending || bridgeQuoteQuery.isError)

  /**
   * The dearest row available at this seat count — every term of the tier the order lands in.
   *
   * `calculateSavings` measures the selected row against it, which is what "hemat paket" means: the
   * same seats over a shorter term would have cost this much more. Passing the selected row itself
   * (as this form used to) makes that figure permanently zero.
   */
  const theMostExpensivePrice = useMemo(
    () =>
      tierPrices.reduce<(typeof tierPrices)[number] | undefined>(
        (dearest, current) => (!dearest || current.price > dearest.price ? current : dearest),
        undefined,
      ),
    [tierPrices],
  )

  const data = useMemo(() => {
    if (!selectedPrice || !theMostExpensivePrice) return null
    return {
      selectedPricePerSeat: selectedPrice.price,
      selectedPriceDuration: selectedPrice.duration,
      theMostExpensivePricePerSeat: theMostExpensivePrice.price,
      totalSeat: seats,
      voucher: selectedVoucher,
      // The bridge is part of the order: the voucher discounts it and the total includes it.
      extraAmount: bridgeTotal,
    }
  }, [seats, selectedPrice, theMostExpensivePrice, selectedVoucher, bridgeTotal])

  // What the volume tier is worth beside what the longer term is worth — see `calculateTierSavings`.
  const tierSavings = selectedPrice ? calculateTierSavings(getAllPrice, selectedPrice, seats) : 0

  // The same two nudges the NEW checkout carries: a cheaper rate is invisible from inside the tier
  // or the term the buyer is already looking at.
  const termUpsell = selectedPrice ? longerTermUpsell(getAllPrice, selectedPrice, seats) : null
  const upsell = selectedPrice ? nextTierUpsell(getAllPrice, selectedPrice, seats) : null

  /**
   * Buy the seats that reach the next tier, as EMPTY seats.
   *
   * Empty, because the students to continue are already ticked — the gap to the boundary is seats
   * the organization is parking for students it has not enrolled yet.
   */
  const handleTakeUpsell = () => {
    if (!upsell) return
    setExtraSeats((current) => current + upsell.seatsNeeded)
  }

  useEffect(() => {
    if (checkoutRenewalMutation.isError) {
      toast.error('Ada error yang terjadi saat ingin mencoba bayar')
    }
  }, [checkoutRenewalMutation.isError])

  const isPending = checkoutRenewalMutation.isPending

  if (!selectedPrice || !data || tierPrices.length === 0) {
    return (
      <section className='srd:flex srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-y-4'>
        <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black srd:text-center')}>
          Tidak ada paket harga tersedia.
        </p>
      </section>
    )
  }

  const totalPrice = calculateTotalPriceWithoutVoucher({ type: 'new', data })

  /**
   * When the seats being bought start, and when they end.
   *
   * The clamp is the lapsed-pool case: renewing a subscription that has already expired is a
   * recovery purchase and starts today, not on a date in the past. The server applies the same
   * clamp when it writes the invoice — this is the display of that answer, not a second opinion.
   */
  const now = new Date()
  const poolEndDate = new Date(currentMembership.endDate)
  const startDate = poolEndDate > now ? poolEndDate : now
  const endDate = offsetDate(startDate, { months: selectedPrice.duration })

  const isBelowMinimum = seats < minSeats

  return (
    <section className='srd:text-neutral-black srd:flex srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-y-4'>
      <section className='srd:flex srd:w-full srd:flex-wrap srd:items-start srd:justify-center srd:gap-8'>
        <section className='srd:flex srd:w-full srd:max-w-[550px] srd:flex-col srd:gap-y-4'>
          <CheckoutCard
            organizationName={organizationData.name}
            title='Perpanjang Premium'
            primaryLine={
              <>
                <AnimatedCurrency value={selectedPrice.price * selectedPrice.duration} /> per kursi
              </>
            }
            secondaryLine={
              <>
                <AnimatedCurrency value={selectedPrice.price} /> per kursi per bulan
              </>
            }
          >
            <section className='srd:flex srd:flex-col srd:gap-y-2'>
              <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>Paket</p>

              {/* CONTROLLED: the tier re-points as the seat count moves, so the field has to be
                  owned here — anything keeping its own id ends up holding one that is no longer in
                  its own narrowed list. Same control as the NEW checkout, because a renewal picks
                  its term against the same rate card and had no reason to read differently. */}
              <PriceCardGroup
                getAllPrice={tierPrices}
                value={selectedPrice.id}
                onValueChange={setSelectedPrice}
                disabled={isPending}
              />

              {termUpsell && (
                <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
                  <button
                    type='button'
                    disabled={isPending}
                    className='srd:text-blue-100 srd:underline srd:disabled:opacity-50'
                    onClick={() => setSelectedPrice(termUpsell)}
                  >
                    Pilih paket {termUpsell.duration} bulan
                  </button>{' '}
                  untuk lebih hemat, {formatCurrency(termUpsell.price)} per kursi per bulan
                </p>
              )}
            </section>

            <section className='srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-x-4 srd:gap-y-2'>
              <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>Total kursi</p>

              {/* The count is a figure, not a control — only "Ubah" opens the dialog. Same shape as
                  the NEW checkout, and the dialog behind it is the same dialog. */}
              <div className='srd:flex srd:items-center srd:gap-x-2'>
                {!isSelectionSettled ? (
                  <Skeleton className='srd:h-6 srd:w-8' />
                ) : (
                  <NumberFlow
                    value={seats}
                    className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}
                  />
                )}

                <Button size='sm' disabled={isPending || !isSelectionSettled} onClick={() => setAssignDialogOpen(true)}>
                  Ubah
                </Button>
              </div>
            </section>

            <AssignSeatDialog
              open={assignDialogOpen}
              onOpenChange={setAssignDialogOpen}
              type={type}
              organizationId={organizationData.id}
              // Continuing students plus students holding no seat; see `offerableChildIds`.
              offerableChildIds={offerableChildIds}
              mode='renewal'
              renewalWindow={{ startDate, endDate }}
              renewalEmptySeatsStartAtPayment={bridgeApplies}
              value={isSelectionSettled ? { childIds: continuedIds ?? [], emptySeats: extraSeats } : null}
              onConfirm={(value: SeatAssignmentValue) => {
                setContinuedIds(value.childIds)
                setExtraSeats(value.emptySeats)
              }}
            />

            {/* The empty seats' pro-rata bridge. Shown only when there is one — a renewal
                continuing everyone and adding nobody prices exactly as it always has. */}
            <CheckoutReveal show={extraSeats > 0 && bridgeApplies}>
              <section className='srd:border-outline-variant srd:flex srd:flex-wrap srd:items-start srd:justify-between srd:gap-x-4 srd:gap-y-2 srd:border-t srd:pt-2'>
                <div className='srd:flex srd:flex-col srd:gap-y-0.5'>
                  <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
                    Kursi kosong aktif lebih awal
                  </p>

                  <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
                    {extraSeats} kursi kosong bisa dipakai sejak pembayaran sampai{' '}
                    {formatDate(endDate, { dateStyle: 'long' })}
                    {bridgeQuote && bridgeQuote.perSeatTotal > 0
                      ? `, pro-rata ${formatCurrency(bridgeQuote.perSeatTotal)} per kursi sampai ${formatDate(startDate, { dateStyle: 'long' })}`
                      : ''}
                    .
                  </p>
                </div>

                {isBridgeUnresolved ? (
                  <Skeleton className='srd:h-6 srd:w-24' />
                ) : (
                  <AnimatedCurrency
                    value={bridgeTotal}
                    className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}
                  />
                )}
              </section>
            </CheckoutReveal>

            <CheckoutReveal show={calculateVoucherDiscount(data) > 0}>
              <section className='srd:border-outline-variant srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-x-4 srd:gap-y-2 srd:border-t srd:pt-2'>
                <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
                  Diskon promo
                </p>

                <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
                  (
                  <AnimatedCurrency
                    value={calculateVoucherDiscount(data)}
                    className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}
                  />
                  )
                </p>
              </section>
            </CheckoutReveal>

            <section className='srd:border-outline-variant srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-x-4 srd:gap-y-2 srd:border-t srd:pt-2'>
              <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>Total</p>

              <AnimatedCurrency
                value={calculateAmountPaid(data)}
                className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-black')}
              />
            </section>

            <section className='srd:mt-4 srd:flex srd:flex-col srd:gap-y-2'>
              {calculateSavings(data) > 0 && (
                <SavingsLine
                  label='Hemat paket'
                  amount={calculateSavings(data)}
                  explanation={SAVINGS_EXPLANATION.term}
                />
              )}

              {tierSavings > 0 && (
                <SavingsLine label='Hemat volume' amount={tierSavings} explanation={SAVINGS_EXPLANATION.tier} />
              )}

              {upsell && (
                <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100 srd:text-right')}>
                  <button
                    type='button'
                    disabled={isPending}
                    className='srd:text-blue-100 srd:underline srd:disabled:opacity-50'
                    onClick={handleTakeUpsell}
                  >
                    Tambah {upsell.seatsNeeded} kursi lagi
                  </button>{' '}
                  untuk lebih hemat, {formatCurrency(upsell.pricePerSeat)} per kursi per bulan
                </p>
              )}
            </section>

            {isBelowMinimum && isSelectionSettled && (
              <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-error srd:text-left')}>
                Minimal {minSeats} kursi untuk melanjutkan.
              </p>
            )}

            {bridgeQuoteQuery.isError && extraSeats > 0 && (
              <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-error srd:text-left')}>
                Gagal menghitung biaya kursi kosong. Coba muat ulang halaman.
              </p>
            )}

            {checkoutRenewalMutation.error && (
              <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-error srd:text-left')}>
                {checkoutRenewalMutation.error.message}
              </p>
            )}
          </CheckoutCard>
        </section>

        <section className='srd:flex srd:w-full srd:max-w-[510px] srd:flex-col srd:gap-y-4'>
          <VoucherList
            className='srd:w-full'
            priceBeforeVoucher={totalPrice}
            onSelectVoucher={(voucher) => setSelectedVoucherId(voucher?.id ?? null)}
            selectedVoucherId={selectedVoucherId}
            selectedPriceId={selectedPrice.id}
            type={type}
            disabled={isPending}
          />

          <section className='srd:border-outline-variant/50 srd:flex srd:w-full srd:flex-col srd:items-center srd:gap-y-2 srd:rounded-xl srd:border srd:p-4'>
            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black srd:text-center')}>
              Merencanakan untuk banyak murid atau rombongan belajar? Kami dapat membantumu dapatkan nilai terbaik
              untukmu.
            </p>
            <Button asChild variant='ghost' size='sm' className='srd:text-neutral-black'>
              <a href={WHATSAPP_LINK} target='_blank' rel='noreferrer'>
                <WhatsappIcon className='srd:mr-2 srd:size-4 srd:shrink-0' />
                Mari berdiskusi
              </a>
            </Button>
          </section>
        </section>
      </section>

      <div className='srd:bg-neutral-white srd:fixed srd:bottom-0 srd:left-0 srd:h-fit srd:w-full srd:shadow-inner srd:z-50'>
        <div className='srd:mx-auto srd:flex srd:w-full srd:max-w-[1440px] srd:flex-wrap srd:items-center srd:justify-between srd:gap-x-6 srd:gap-y-2 srd:px-2 srd:py-4'>
          <section className='srd:flex srd:flex-col srd:gap-y-0'>
            <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>Total</p>

            <AnimatedCurrency
              value={calculateAmountPaid(data)}
              className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-neutral-black')}
            />

            {calculateTotalDiscount(data, tierSavings) > 0 && (
              <p
                className={cn(
                  typographyVariants({ variant: 'body-md' }),
                  'srd:text-purple-100 srd:flex srd:items-center srd:gap-x-1',
                )}
              >
                Total hemat
                <AnimatedCurrency
                  value={calculateTotalDiscount(data, tierSavings)}
                  className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-purple-100')}
                />
              </p>
            )}
          </section>

          <Button
            variant='gradient'
            size='lg'
            loading={isPending}
            disabled={isPending || !isSelectionSettled || isBelowMinimum || isBridgeUnresolved}
            onClick={() => {
              checkoutRenewalMutation.mutate({
                organizationId: organizationData.id,
                type: 'RENEWAL',
                seats,
                totalPrice,
                amountPaid: calculateAmountPaid(data),
                priceId: selectedPrice.id,
                voucherId: selectedVoucherId ?? undefined,
                totalDiscount: calculateVoucherDiscount(data),
                // Who continues. Re-validated server-side and replayed at fulfilment, which stamps
                // these children onto the seats it mints.
                assignToChildIds: continuedIds ?? [],
                // The pool being continued: it supplies the start date and nothing else.
                renewalMembershipIds: [currentMembership.id],
                withXendit: calculateAmountPaid(data) > 0,
                xenditConfig: {
                  membershipId: currentMembership.id,
                },
              })
            }}
          >
            Konfirmasi
          </Button>
        </div>
      </div>
    </section>
  )
}
